// src/frontend/js/modern/modules/wishlist/ui.js
import { BaseListUI } from '../../shared/base-list-ui.js';
import { showSuccess } from '../../shared/ui-helpers.js';
import { showPopup } from '../../lib/popup-lite.js';
import wishlistModuleStore from './store.js';
import stateCache from "../../core/state-cache";
import fragmentManager from "../../core/fragment-manager";

class WishlistUI extends BaseListUI {
	constructor() {
		super({
			moduleName: 'wishlist',
			store: wishlistModuleStore,
			selectors: {
				wishlistTable: '.wlfmc-wishlist-table',
				tableWrapper: '.wlfmc-wishlist-table-wrapper',
				miniWishlist: '.wlfmc-products-counter-wrapper',
			}
		});
	}
	/* ═══════════════════════════════════════════
	   REACTIVE RENDER — Single Source of Truth
	   Overrides BaseListUI to also reconcile
	   product-page add-to-wishlist buttons.
	════════════════════════════════════════════ */
	renderFromState() {
		const items = stateCache.getItems(this.moduleName);
		const count = stateCache.getCount(this.moduleName);
		this.syncCounterItems(items);
		this.updateCounter(count);
		this.syncAddButtons(items);
	}
	/**
	 * Reconcile all add-to-wishlist buttons on product pages with the
	 * current StateCache snapshot. Adds/removes the 'exists' class and
	 * patches delete-button dataset attributes.
	 *
	 * In merge mode, a product is considered "exists" if it appears in
	 * ANY multi-list, not just the default wishlist.
	 */
	syncAddButtons(items) {
		const isMerge = stateCache.getConfig()?.mergeLists === true;

		const currentProductIds = new Set(
			(items || []).map(item => String(item.product_id))
		);
		const itemMap = new Map();
		(items || []).forEach(item => {
			itemMap.set(String(item.product_id), item);
		});

		document.querySelectorAll('.wlfmc-add-to-wishlist').forEach(el => {
			const cls = Array.from(el.classList).find(c => c.startsWith('wlfmc-add-to-wishlist-'));
			const pid = cls ? cls.replace('wlfmc-add-to-wishlist-', '') : (el.dataset.productId || '');
			if (!pid) return;

			// In merge mode, check if the product exists in ANY list.
			// In non-merge mode, check only the default wishlist items.
			const shouldBeExists = isMerge
				? stateCache.isProductInAnyList(pid)
				: currentProductIds.has(String(pid));

			const isCurrentlyExists = el.classList.contains('exists');
			if (shouldBeExists && !isCurrentlyExists) {
				el.classList.add('exists');
				const item = itemMap.get(String(pid));
				if (item?.item_id) {
					const deleteBtn = el.querySelector('.wlfmc_delete_item');
					if (deleteBtn) {
						deleteBtn.dataset.itemId = item.item_id;
						deleteBtn.dataset.wishlistId = item.wishlist_id;
					}
				}
			} else if (!shouldBeExists && isCurrentlyExists) {
				el.classList.remove('exists');
			}
		});
	}
	/**
	 * Refresh the wishlist TABLE content via targeted fragment loading.
	 * Delegates to FragmentManager which handles the admin-ajax call,
	 * DOM replacement, and re-initialization automatically.
	 */
	refreshTableFromServer() {
		fragmentManager.loadFragments({ s: 'wishlist' });
	}
	updateAddButton(productId, data = {}) {
		const selector = `.wlfmc-add-to-wishlist-${productId}`;
		document.querySelectorAll(selector).forEach((el) => {
			el.classList.add('exists');
			if (data.item_id) {
				const removeBtn = el.querySelector('.wlfmc_delete_item');
				if (removeBtn) {
					removeBtn.dataset.itemId = data.item_id;
					removeBtn.dataset.wishlistId = data.wishlist_id || '';
				}
			}
			const afterSecondClick = el.dataset.afterSecondClick || 'remove';
			el.classList.remove('show-remove-after-add', 'show-browse-after-add', 'show-exists-after-add');
			el.classList.add(`show-${afterSecondClick}-after-add`);
		});
		this.showTableRow(productId);
		this.showMiniWishlistItem(productId);
	}
	updateRemoveButton(productId) {
		const isMerge = stateCache.getConfig()?.mergeLists === true;
		const selector = `.wlfmc-add-to-wishlist-${productId}`;

		if (isMerge) {
			// In merge mode, only remove 'exists' if the product is no longer
			// in ANY list. Removing from one list doesn't necessarily mean
			// the product is gone from all lists.
			const stillInAnyList = stateCache.isProductInAnyList(productId);
			if (!stillInAnyList) {
				document.querySelectorAll(selector).forEach((el) => {
					el.classList.remove('exists');
				});
				//this.hideTableRow(productId);
				this.hideMiniWishlistItem(productId);
			}
		} else {
			document.querySelectorAll(selector).forEach((el) => {
				el.classList.remove('exists');
			});
			//this.hideTableRow(productId);
			this.hideMiniWishlistItem(productId);
		}
	}
	hideTableRow(productId) {
		document.querySelectorAll(`${this._selectors.wishlistTable} [data-row-id="${productId}"]`).forEach((row) => { row.style.display = 'none'; });
	}
	showTableRow(productId) {
		document.querySelectorAll(`${this._selectors.wishlistTable} [data-row-id="${productId}"]`).forEach((row) => { row.style.display = ''; });
	}
	hideMiniWishlistItem(productId) {
		document.querySelectorAll(`${this._selectors.miniWishlist} [data-row-id="${productId}"]`).forEach((item) => { item.style.display = 'none'; });
	}
	showMiniWishlistItem(productId) {
		document.querySelectorAll(`${this._selectors.miniWishlist} [data-row-id="${productId}"]`).forEach((item) => { item.style.display = ''; });
	}
	updatePopupContent(popup, data = {}, productTitle = '', originalPrice = '') {
		if (!popup) return;
		const popupData = popup.dataset;
		const titleTemplate = window.wlfmc_l10n?.labels?.popup_title;
		const descTemplate = window.wlfmc_l10n?.labels?.popup_content;
		const price = data.price_html || originalPrice ||
			popup.querySelector('.wlfmc-parent-product-price')?.innerHTML || '';
		const title = productTitle || popupData.productTitle || '';
		let titleHtml = titleTemplate
			.replace(/{product_name}/g, title)
			.replace(/{product_price}/g, price);
		let descHtml = descTemplate
			.replace(/{product_name}/g, title)
			.replace(/{product_price}/g, price);
		const titleEl = popup.querySelector('.wlfmc-popup-title');
		const descEl = popup.querySelector('.wlfmc-popup-desc');
		if (titleEl) titleEl.innerHTML = titleHtml;
		if (descEl) descEl.innerHTML = descHtml;
		const img = popup.querySelector('.wlfmc-popup-header img');
		if (img) {
			const useFeatured = popupData.useFeatured === 'true';
			const imageSize = popupData.imageSize || 'large';
			let imageSrc = img.dataset.src || img.src;
			if (useFeatured && data.image_id) {
				if (imageSize === 'large' && data.image?.full_src) imageSrc = data.image.full_src;
				else if (imageSize === 'thumbnail' && data.image?.thumb_src) imageSrc = data.image.thumb_src;
				else if (data.image?.src) imageSrc = data.image.src;
			}
			img.src = imageSrc;
		}
		const priceHidden = popup.querySelector('.wlfmc-parent-product-price');
		if (priceHidden) priceHidden.innerHTML = price;
	}
	showWishlistPopupIfNeeded(element, productId) {
		const elWrap = element.closest(`.wlfmc-add-to-wishlist-${productId}`);
		if (!elWrap) return;
		const popupId = elWrap.dataset.popupId;
		if (!popupId && window.wlfmc_l10n?.labels?.product_added_text) {
			showSuccess(window.wlfmc_l10n.labels.product_added_text);
		}
		if (!popupId) {
			return;
		}
		const popup = document.getElementById(popupId);
		if (!popup) return;
		const tooltip = document.getElementById('wlfmc-tooltip');
		if (tooltip) {
			tooltip.style.top = '0';
			tooltip.style.left = '0';
			tooltip.className = 'tooltip__hidden';
		}
		const productTitle = popup.dataset.productTitle || '';
		const originalPrice = popup.querySelector('.wlfmc-parent-product-price')?.innerHTML || '';
		const variationData = {
			price_html: originalPrice,
			image: { src: popup.querySelector('.wlfmc-popup-header img')?.src || '' },
		};
		this.updatePopupContent(popup, variationData, productTitle, originalPrice);
		showPopup(popup);
	}
}
export const wishlistUI = new WishlistUI();
export default wishlistUI;
